export type EntityQueryKey = (string | Record<string, unknown>)[];

/**
 * The key root every query/invalidation for an entity shares. Splitting the
 * path into segments makes nested paths ("auth/roles", "users/123") natural
 * children of their entity prefix, so one `invalidateQueries({ queryKey:
 * entityKeyRoot(serviceName) })` reaches lists, details, and sub-resources.
 *
 * A leading URI-version segment ("/v1/users", "/v2/users") is stripped: the
 * version belongs to the transport path, not the cache identity — the same
 * entity fetched through different API versions shares one key root, so a
 * mutation invalidates it regardless of which version each query used.
 */
export const entityKeyRoot = (entityType: string): string[] =>
  entityType
    .split("/")
    .filter((segment) => segment.length > 0)
    .filter((segment, index) => !(index === 0 && /^v\d+$/.test(segment)));

/**
 * Params are folded in as a single key-sorted object rather than spread
 * values: value-only keys collide (`{page: 1}` vs `{limit: 1}`), and React
 * Query's default hasher already serializes object keys deterministically.
 */
export const generateEntityQueryKey = ({
  entityType,
  params,
  version = 1,
}: {
  entityType: string;
  params?: Record<string, unknown>;
  version?: number;
}): EntityQueryKey => {
  const key: EntityQueryKey = [...entityKeyRoot(entityType), `v${version}`];

  const definedEntries = Object.entries(params ?? {})
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b));

  if (definedEntries.length > 0) {
    key.push(Object.fromEntries(definedEntries));
  }

  return key;
};
